// ============================================================================
// 🚀 PUESTA EN MARCHA — Asistente de configuración inicial de la empresa.
// ============================================================================
// Guía paso a paso la parametrización completa del ERP, agrupada en fases.
// Cada paso se verifica AUTOMÁTICAMENTE cuando es posible (lee el estado real
// de la configuración/datos) o se marca manualmente cuando requiere criterio
// humano (ej: "revisar el PUC con el contador"). Los pasos opcionales pueden
// marcarse como "No aplica". El avance se guarda en la nube (tenant_config)
// para que todos los admins vean el mismo progreso.
import { C, EMPRESA } from "../core/config.js";
import { getPuestaEnMarcha, savePuestaEnMarcha } from "../core/cloudSync.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML } from "../core/utils.js";

const NAVY = C.mathiz;
const GOLD = C.gold;

// ─── Definición de pasos ────────────────────────────────────────────────────
// check(): true si el paso se detecta completado automáticamente.
// Sin check → paso manual (botón "Marcar como completado").
// opcional: true → puede marcarse "No aplica" y sale del cálculo de progreso.
const PASOS = [
  // FASE 1
  {
    fase: "1 · Identidad y datos fiscales",
    id: "empresa", icon: "🏢", titulo: "Datos de la empresa",
    desc: "Razón social, NIT, dirección, contacto y perfil tributario (régimen de IVA, gran contribuyente, autorretenedor, actividad CIIU). Aparecen en todos los documentos impresos y definen cómo se causan los impuestos. Se completa cuando la razón social y el NIT estén diligenciados.",
    pagina: "config", paginaLabel: "Configuración",
    check: () => !!(EMPRESA.nombre && EMPRESA.nit)
  },
  {
    id: "logo", icon: "🖼️", titulo: "Cargar el logo de la empresa",
    desc: "Sube el logo en Configuración: aparece en la pantalla de inicio de sesión, el dashboard, los encabezados de cada módulo y en todos los documentos impresos (cotizaciones, facturas, remisiones).",
    pagina: "config", paginaLabel: "Configuración",
    check: () => !!EMPRESA.logo
  },
  {
    id: "fiscales", icon: "📐", titulo: "Parámetros fiscales del año",
    desc: "UVT vigente, tarifa general de IVA, tarifas y bases mínimas de retención en la fuente y reteIVA. Sin esto, la causación de facturas de proveedor calcula mal las retenciones.",
    pagina: "parametros-fiscales", paginaLabel: "Parámetros Fiscales",
    check: () => { try { return !!localStorage.getItem("erp:parametros_fiscales"); } catch { return false; } }
  },
  {
    // Numeración y resolución viven en la MISMA pantalla (Parámetros Fiscales):
    // se unifican en un solo paso para no enviar dos veces a la misma página.
    id: "facturacion_numeracion", icon: "🔢", titulo: "Numeración y resolución de facturación",
    desc: "En Parámetros Fiscales, en una sola pantalla, defines: (1) los consecutivos de cada documento (CT, OP, OCP, RS, FE, NC) para continuar tu numeración histórica y no repetir números —si empiezas de cero, arranca en 0001—, y (2) si emites factura electrónica, la resolución DIAN vigente (número, prefijo, rango y fechas), con alerta automática antes de que venza. Se marca solo al registrar una resolución activa; si solo defines la numeración, márcalo como completado.",
    pagina: "parametros-fiscales", paginaLabel: "Parámetros Fiscales",
    permiteManual: true,
    check: () => (state.data.resoluciones_dian || []).some(r => r.activa)
  },
  {
    id: "facturacion_electronica", icon: "⚡", titulo: "Conectar la facturación electrónica", opcional: true,
    desc: "Si emites factura electrónica DIAN a través de Dataico: configura la conexión (cuenta y credenciales) desde el módulo de Facturación para emitir directamente desde el ERP.",
    pagina: "FACT", paginaLabel: "Facturación",
    check: () => { try { return !!localStorage.getItem("erp:dataico_config"); } catch { return false; } }
  },
  // FASE 2
  {
    fase: "2 · Contabilidad",
    id: "puc", icon: "🧮", titulo: "Revisar el plan de cuentas (PUC)",
    desc: "El ERP trae el PUC estándar colombiano (Decreto 2650) y causa automáticamente ventas, compras, nómina y depreciación. Revisa con tu contador que las cuentas se ajusten a tu operación y agrega las que falten.",
    pagina: "contabilidad", paginaLabel: "Contabilidad"
  },
  {
    id: "saldos_iniciales", icon: "⚖️", titulo: "Registrar los saldos iniciales", opcional: true,
    desc: "Si vienes de otro sistema o de contabilidad manual: registra el asiento de apertura (bancos, cartera, inventarios, activos, pasivos y patrimonio) con corte al día anterior al arranque, para que los estados financieros salgan completos desde el primer día.",
    pagina: "contabilidad", paginaLabel: "Contabilidad"
  },
  {
    id: "activos", icon: "🏢", titulo: "Registrar los activos fijos", opcional: true,
    desc: "Propiedad, planta y equipo con costo, vida útil y fecha de compra. El ERP calcula y causa la depreciación mensual automáticamente.",
    pagina: "activos-fijos", paginaLabel: "Activos Fijos",
    check: () => (state.data.activos_fijos || []).length > 0
  },
  // FASE 3
  {
    fase: "3 · Equipo y seguridad",
    id: "empleados", icon: "🧑‍💼", titulo: "Registrar los empleados",
    desc: "Al menos los vendedores (para asignarlos como asesores comerciales) y quienes entren a nómina, con salario, afiliaciones (EPS, AFP, ARL) y datos bancarios.",
    pagina: "empleados", paginaLabel: "Empleados",
    check: () => (state.data.empleados || []).length > 0
  },
  {
    id: "usuarios", icon: "👥", titulo: "Crear usuarios y roles de acceso",
    desc: "Crea el usuario de cada persona del equipo y asígnale un rol (del sistema o personalizado con permisos por módulo). Nunca compartan una misma cuenta: la auditoría y la medición de uso son por usuario.",
    pagina: "usuarios", paginaLabel: "Usuarios"
  },
  {
    id: "nomina", icon: "🧑‍🌾", titulo: "Parámetros de nómina", opcional: true,
    desc: "SMLMV, auxilio de transporte, porcentajes de seguridad social y parafiscales, niveles de ARL y exoneración Ley 1607 si aplica. Necesario solo si liquidarás nómina en el ERP.",
    pagina: "nomina", paginaLabel: "Nómina",
    check: () => { try { return !!localStorage.getItem("erp:parametros_nomina"); } catch { return false; } }
  },
  {
    id: "comisiones", icon: "🏆", titulo: "Tabla de comisiones", opcional: true,
    desc: "Rangos de vendedor y porcentajes de comisión por nivel de margen. Necesaria solo si pagas comisiones sobre ventas.",
    pagina: "comisiones", paginaLabel: "Comisiones",
    check: () => { try { return !!localStorage.getItem("erp:tabla_comisiones"); } catch { return false; } }
  },
  // FASE 4
  {
    fase: "4 · Operación",
    id: "productos", icon: "📦", titulo: "Cargar el catálogo de productos",
    desc: "Cada producto con costo, margen e imagen (la imagen sale en las cotizaciones impresas). El precio de venta se calcula solo a partir del costo y el margen.",
    pagina: "maestros", paginaLabel: "Maestros",
    check: () => (state.data.productos || []).length > 0
  },
  {
    id: "clientes", icon: "👥", titulo: "Cargar los clientes",
    desc: "Con NIT, datos tributarios (para las retenciones que te aplican), días de pago y personas de contacto. El asesor comercial asignado se hereda a todos sus documentos.",
    pagina: "maestros", paginaLabel: "Maestros",
    check: () => (state.data.clientes || []).length > 0
  },
  {
    id: "proveedores", icon: "🏭", titulo: "Cargar los proveedores",
    desc: "Con su ficha tributaria (define las retenciones al causar sus facturas), días de pago y descuentos por pronto pago si los ofrecen.",
    pagina: "maestros", paginaLabel: "Maestros",
    check: () => (state.data.proveedores || []).length > 0
  },
  {
    id: "inventario_inicial", icon: "📦", titulo: "Cargar el inventario inicial", opcional: true,
    desc: "Si manejas stock: carga las existencias iniciales de cada producto (por ingreso de órdenes de compra o ajuste de inventario) para que el kárdex y el costo promedio arranquen con la realidad de tu bodega.",
    pagina: "inventario", paginaLabel: "Inventario",
    check: () => (state.data.inventario || []).some(i => (i.stock || 0) > 0)
  },
  {
    id: "alertas", icon: "🔔", titulo: "Configurar las alertas", opcional: true,
    desc: "Umbrales de días para entregas, seguimiento de cotizaciones y vencimientos, y los números de WhatsApp a los que se envían.",
    pagina: "alertas", paginaLabel: "Centro de Alertas",
    check: () => ((state.alertConfig || {}).whatsappNumbers || []).length > 0
  },
  {
    id: "respaldo", icon: "💾", titulo: "Descargar el primer respaldo",
    desc: "Aunque todo vive en la nube, descarga un respaldo JSON inicial y agenda hacerlo periódicamente. El dashboard te lo recordará cada 7 días.",
    pagina: "respaldos", paginaLabel: "Respaldos",
    check: () => { try { return !!localStorage.getItem("erp:last_backup"); } catch { return false; } }
  }
];

// ─── Estado de los pasos ────────────────────────────────────────────────────
function estadoPasos() {
  const cfg = getPuestaEnMarcha();
  const manual = cfg.manual || {};
  const noAplica = cfg.noAplica || {};
  return PASOS.map(p => {
    const na = p.opcional && !!noAplica[p.id];
    const autoOk = p.check ? !!(() => { try { return p.check(); } catch { return false; } })() : false;
    // Un paso puede completarse por detección automática y/o por marca manual.
    // permiteManual: paso con verificación automática que además se puede marcar
    // a mano (ej. numeración: la resolución se autodetecta, pero quien solo fija
    // consecutivos necesita marcarlo). esManual controla si se ofrece el botón.
    const puedeManual = !p.check || !!p.permiteManual;
    const manualOk = puedeManual && !!manual[p.id];
    const completado = !na && (autoOk || manualOk);
    return { ...p, na, completado, autoOk, esManual: puedeManual };
  });
}

export function calcularProgresoPuestaEnMarcha() {
  const pasos = estadoPasos();
  const relevantes = pasos.filter(p => !p.na);
  const completados = relevantes.filter(p => p.completado).length;
  return {
    total: relevantes.length,
    completados,
    pct: relevantes.length ? Math.round((completados / relevantes.length) * 100) : 100,
    ocultarBanner: !!getPuestaEnMarcha().ocultarBanner
  };
}

// ─── Render ─────────────────────────────────────────────────────────────────
export function renderPuestaEnMarcha() {
  const pasos = estadoPasos();
  const prog = calcularProgresoPuestaEnMarcha();
  const listo = prog.pct === 100;

  const header = `
    <div style="background:linear-gradient(135deg,${NAVY},#0B132B);border-radius:16px;padding:26px 28px;margin-bottom:22px;color:white;position:relative;overflow:hidden;">
      <div style="position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,${GOLD},transparent);"></div>
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px;">
        <div>
          <h2 style="margin:0;font-size:16px;font-weight:800;">🚀 Puesta en marcha</h2>
          <p style="margin:6px 0 0;font-size:12px;opacity:.85;max-width:560px;">Configura la empresa paso a paso para operar el ERP correctamente. Los pasos con verificación automática se marcan solos cuando detectamos la configuración; los demás márcalos tú al completarlos.</p>
        </div>
        <div style="text-align:center;min-width:130px;">
          <div style="font-size:34px;font-weight:800;color:${GOLD};">${prog.pct}%</div>
          <div style="font-size:10.5px;opacity:.8;">${prog.completados} de ${prog.total} pasos</div>
        </div>
      </div>
      <div style="margin-top:16px;background:rgba(255,255,255,.15);border-radius:6px;height:12px;overflow:hidden;">
        <div style="width:${prog.pct}%;height:100%;background:linear-gradient(90deg,${GOLD},#e8c05a);border-radius:6px;transition:width .3s;"></div>
      </div>
      ${listo ? `<div style="margin-top:14px;background:rgba(16,185,129,.2);border:1px solid rgba(16,185,129,.5);border-radius:6px;padding:7px 10px;font-size:12px;font-weight:700;">🎉 ¡Configuración completa! La empresa está lista para operar.</div>` : ""}
    </div>
  `;

  let faseActual = "";
  const cuerpo = pasos.map(p => {
    const tituloFase = p.fase && p.fase !== faseActual ? (faseActual = p.fase, `
      <div style="display:flex;align-items:center;gap:10px;margin:22px 0 10px;">
        <span style="font-size:11.5px;font-weight:800;letter-spacing:.8px;color:${NAVY};">FASE ${p.fase.toUpperCase()}</span>
        <span style="flex:1;height:2px;background:linear-gradient(90deg,${NAVY}40,transparent);border-radius:2px;"></span>
      </div>`) : "";

    const estado = p.na
      ? `<span style="background:${C.border}60;color:${C.muted};padding:4px 12px;border-radius:14px;font-size:10.5px;font-weight:700;white-space:nowrap;">➖ No aplica</span>`
      : p.completado
        ? `<span style="background:#d1fae5;color:#059669;padding:4px 12px;border-radius:14px;font-size:10.5px;font-weight:700;white-space:nowrap;">✅ Completado</span>`
        : `<span style="background:#fef3c7;color:#d97706;padding:4px 12px;border-radius:14px;font-size:10.5px;font-weight:700;white-space:nowrap;">⬜ Pendiente</span>`;

    const botones = `
      <button data-nav="${p.pagina}" style="${btnStyle(NAVY, null, true)}">⚙️ ${escapeHTML(p.paginaLabel)} →</button>
      ${p.esManual && !p.na && !p.autoOk ? `
        <button data-action="pm-toggle-manual" data-id="${p.id}" style="${btnStyle(p.completado ? null : "#059669", p.completado ? "secondary" : null, true)}">
          ${p.completado ? "↩️ Desmarcar" : "✔️ Marcar como completado"}
        </button>` : ""}
      ${p.opcional ? `
        <button data-action="pm-toggle-na" data-id="${p.id}" style="${btnStyle(null, "ghost", true)}">
          ${p.na ? "↩️ Sí aplica" : "➖ No aplica"}
        </button>` : ""}
    `;

    return tituloFase + `
      <div style="background:white;border:1px solid ${p.completado ? "#a7f3d0" : C.border};border-left:4px solid ${p.na ? C.border : p.completado ? "#059669" : GOLD};border-radius:6px;padding:16px 18px;margin-bottom:10px;${p.na ? "opacity:.6;" : ""}">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap;">
          <div style="flex:1;min-width:260px;">
            <div style="font-size:14px;font-weight:800;color:${C.text};">${p.icon} ${escapeHTML(p.titulo)}${p.opcional ? ` <span style="font-size:10px;font-weight:600;color:${C.muted};">(opcional)</span>` : ""}${!p.esManual ? ` <span title="Este paso se verifica automáticamente" style="font-size:10px;font-weight:700;color:${NAVY};background:${NAVY}12;padding:1px 7px;border-radius:6px;">AUTO</span>` : ""}</div>
            <div style="font-size:11.5px;color:${C.muted};margin-top:5px;line-height:1.55;max-width:640px;">${escapeHTML(p.desc)}</div>
          </div>
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:8px;">
            ${estado}
            <div style="display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end;">${botones}</div>
          </div>
        </div>
      </div>
    `;
  }).join("");

  return header + cuerpo + `
    <div style="margin-top:14px;padding:9px;background:#f8fafc;border:1px solid #c7d2fe;border-radius:6px;font-size:11.5px;color:#3730a3;">
      💡 Los pasos <strong>AUTO</strong> se verifican solos: configura en el módulo correspondiente y al volver aquí aparecerán completados. El progreso se comparte entre todos los administradores.
    </div>
  `;
}

// Banner para el dashboard mientras la configuración esté incompleta (solo admin).
export function bannerPuestaEnMarcha() {
  if (state.usuarioRol && state.usuarioRol !== "admin") return "";
  const prog = calcularProgresoPuestaEnMarcha();
  if (prog.pct === 100 || prog.ocultarBanner) return "";
  return `
    <div style="background:linear-gradient(135deg,${NAVY}10,${GOLD}10);border:1.5px solid ${NAVY}35;border-radius:6px;padding:12px 16px;margin-bottom:10px;display:flex;justify-content:space-between;align-items:center;gap:9px;flex-wrap:wrap;">
      <div data-nav="puesta-en-marcha" style="display:flex;gap:9px;align-items:center;cursor:pointer;flex:1;min-width:240px;">
        <span style="font-size:16px;">🚀</span>
        <div style="flex:1;">
          <div style="font-weight:700;color:${NAVY};font-size:12px;">Puesta en marcha: ${prog.pct}% — ${prog.completados} de ${prog.total} pasos</div>
          <div style="margin-top:5px;background:${C.border};border-radius:6px;height:7px;overflow:hidden;max-width:340px;">
            <div style="width:${prog.pct}%;height:100%;background:linear-gradient(90deg,${NAVY},${GOLD});border-radius:6px;"></div>
          </div>
        </div>
      </div>
      <div style="display:flex;gap:8px;">
        <button data-nav="puesta-en-marcha" style="${btnStyle(NAVY, null, true)}">Continuar configuración →</button>
        <button data-action="pm-ocultar-banner" title="Ocultar este aviso" style="${btnStyle(null, "ghost", true)}">✕</button>
      </div>
    </div>
  `;
}

// ─── Acciones ───────────────────────────────────────────────────────────────
import { registerActions } from "../ui/actions.js";
import { render } from "../ui/app.js";

registerActions({
  "pm-toggle-manual": (d) => {
    const cfg = getPuestaEnMarcha();
    cfg.manual = cfg.manual || {};
    cfg.manual[d.id] = !cfg.manual[d.id];
    savePuestaEnMarcha(cfg);
    render();
  },
  "pm-toggle-na": (d) => {
    const cfg = getPuestaEnMarcha();
    cfg.noAplica = cfg.noAplica || {};
    cfg.noAplica[d.id] = !cfg.noAplica[d.id];
    savePuestaEnMarcha(cfg);
    render();
  },
  "pm-ocultar-banner": () => {
    const cfg = getPuestaEnMarcha();
    cfg.ocultarBanner = true;
    savePuestaEnMarcha(cfg);
    render();
  }
});
